import { getInitialPlan } from 'screens/BrandPlanSchedule/utils'
import { FormValues } from './types'
import { BrandPlan } from 'types'

// All channel content keys
export const CONTENT_KEYS = [
  'facebook_contents',
  'twitter_contents',
  'linkedin_contents',
  'instagram_post_contents',
  'instagram_reel_contents',
  'instagram_story_contents',
  'tiktok_contents',
  'youtube_contents',
  'seo_contents',
  'email_contents',
  'crosspost_contents',
  'video_crosspost_contents',
] as const

export const buildInitialValues = (
  brandPlan: BrandPlan | undefined,
  maxChannels: number,
  maxPostsPerChannel: number,
) => {
  const baseValues = getInitialPlan(brandPlan)
  const values: FormValues = {
    ...baseValues,
    checked_channels: [],
  }

  // Calculate total count for each channel (using original counts, before capping)
  const channelsWithCounts = CONTENT_KEYS.filter(
    (key) => key !== 'crosspost_contents' && key !== 'video_crosspost_contents',
  )
    .map((key) => {
      const content = values[key]
      const totalCount = content?.reduce((sum, item) => sum + item.count, 0) || 0
      return { key, totalCount }
    })
    .filter(({ totalCount }) => totalCount > 0)
    .sort((a, b) => b.totalCount - a.totalCount)

  // Take the top channels, but cap at maxChannels
  const checked_channels: string[] = channelsWithCounts.slice(0, maxChannels).map(({ key }) => key)

  // Cap checked channels' counts at maxPostsPerChannel
  checked_channels.forEach((key) => {
    const content = values[key as keyof typeof values] as { type: string; count: number }[] | undefined
    if (content && Array.isArray(content)) {
      const cappedContent = content.map((item) => ({
        ...item,
        count: Math.min(item.count, maxPostsPerChannel),
      }))
      ;(values as any)[key] = cappedContent
    }
  })

  // Set all unchecked channels to 0
  CONTENT_KEYS.forEach((key) => {
    if (!checked_channels.includes(key)) {
      const content = values[key as keyof typeof values] as { type: string; count: number }[] | undefined
      if (content && Array.isArray(content)) {
        ;(values as any)[key] = content.map((item) => ({ ...item, count: 0 }))
      }
    }
  })

  return { ...values, checked_channels }
}

export const getUsedChannelsCount = (checkedChannels: string[], disabledChannels: string[] = []): number => {
  return checkedChannels.filter((channel) => !disabledChannels.includes(channel)).length
}

export const buildLimitedChannels = (formValues: FormValues) => {
  const { checked_channels } = formValues

  // Helper to zero out counts in a content array
  const zeroOutCounts = (contents: { type: string; count: number }[] | undefined) => {
    return contents?.map((item) => ({ ...item, count: 0 })) || []
  }

  // Build payload: if channel is checked, keep its value; otherwise zero it out
  const payload: any = {}
  CONTENT_KEYS.forEach((key) => {
    const content = formValues[key as keyof FormValues] as { type: string; count: number }[] | undefined
    payload[key] = checked_channels.includes(key) ? content : zeroOutCounts(content)
  })

  // Merge Instagram contents for the API (expects single instagram_contents field)
  payload.instagram_contents = [
    ...(payload.instagram_post_contents || []),
    ...(payload.instagram_story_contents || []),
    ...(payload.instagram_reel_contents || []),
  ]
  delete payload.instagram_post_contents
  delete payload.instagram_story_contents
  delete payload.instagram_reel_contents

  return payload
}
